/* eslint-disable no-console */
import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Box, Button, Text } from '@campaignhub/suit-theme'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft, faChevronRight, faDownload, faExternalLink, faEyeSlash, faTimes,
} from '@fortawesome/pro-light-svg-icons'

export const REDUCED_RESOLUTION_DISCLAIMER = 'This image is displayed at a reduced resolution. '
  + 'Download it to view the full-resolution version in an external app.'

// A preview item is the normalised shape every tab maps its rows into:
// { key, name, downloadName, previewUrl, videoUrl, downloadUrl, href, reducedResolution }. The
// reduced-resolution notice shows by default (generated previews are lowered); a full-resolution
// source sets it false.
const FilePreviewOverlay = ({ item, items, onClose, onNavigate }) => {
  const previewUrl = item?.previewUrl
  const videoUrl = item?.videoUrl
  const itemKey = item?.key
  const canNavigate = items.length > 1
  const currentIndex = items.findIndex(previewItem => previewItem.key === itemKey)

  const onPrev = useCallback(() => {
    if (currentIndex < 0) {
      return
    }

    const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1
    onNavigate(items[newIndex])
  }, [currentIndex, items, onNavigate])

  const onNext = useCallback(() => {
    if (currentIndex < 0) {
      return
    }

    const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1
    onNavigate(items[newIndex])
  }, [currentIndex, items, onNavigate])

  const onDownload = useCallback(async () => {
    try {
      const response = await fetch(item.downloadUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', item.downloadName || item.name || 'file')
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }, [item])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        onPrev()
      } else if (e.key === 'ArrowRight') {
        onNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, onNext, onPrev])

  if (!item) {
    return null
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      width="100%"
      height="100%"
      backgroundColor="rgba(0, 0, 0, 0.75)"
      display="flex"
      justifyContent="center"
      alignItems="center"
      zIndex={9999}
    >
      <Box
        flexDirection="row"
        position="absolute"
        top={20}
        right={0}
        width="100%"
        alignItems="center"
        justifyContent="flex-end"
        paddingX="large"
        zIndex={10000}
      >
        <Text color="white" fontSize="small" style={{ marginRight: 'auto' }}>
          {item.name}
        </Text>
        {item.downloadUrl && (
          <Button
            onClick={onDownload}
            cursor="pointer"
            borderRadius="50%"
            buttonStyle="primaryUtility"
            icon={<FontAwesomeIcon icon={faDownload} size="lg" />}
            size="small"
            width="40px"
            height="40px"
            title="Download"
            style={{ marginRight: 4 }}
          />
        )}
        {item.href && (
          <Button
            as="a"
            href={item.href}
            cursor="pointer"
            borderRadius="50%"
            buttonStyle="primaryUtility"
            icon={<FontAwesomeIcon icon={faExternalLink} size="lg" />}
            size="small"
            width="40px"
            height="40px"
            title="Open"
            style={{ marginRight: 4 }}
          />
        )}
        <Button
          onClick={onClose}
          cursor="pointer"
          borderRadius="50%"
          buttonStyle="primaryUtility"
          icon={<FontAwesomeIcon icon={faTimes} size="lg" />}
          size="small"
          width="40px"
          height="40px"
          title="Close"
        />
      </Box>
      {canNavigate && (
        <Box
          position="absolute"
          left={30}
          top="calc(50% - 40px)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="auto"
          zIndex={10000}
        >
          <Button
            buttonStyle="primaryUtility"
            icon={<FontAwesomeIcon icon={faChevronLeft} size="2x" />}
            onClick={onPrev}
            size="small"
            borderRadius="50%"
            width="45px"
            height="45px"
            title="Previous"
          />
        </Box>
      )}
      {videoUrl ? (
        <video
          key={itemKey}
          controls
          style={{ width: '100%', height: '100%' }}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : previewUrl ? (
        <img
          key={itemKey}
          alt={item.name || ''}
          src={previewUrl}
          style={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
        />
      ) : (
        <Box alignItems="center" display="flex" flexDirection="column" justifyContent="center" width="100%">
          <FontAwesomeIcon color="white" icon={faEyeSlash} size="3x" />
          <Text color="white" fontSize="small" marginTop="medium">No preview available for this file.</Text>
        </Box>
      )}
      {canNavigate && (
        <Box
          position="absolute"
          right={30}
          top="calc(50% - 40px)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="auto"
          zIndex={10000}
        >
          <Button
            buttonStyle="primaryUtility"
            icon={<FontAwesomeIcon icon={faChevronRight} size="2x" />}
            onClick={onNext}
            size="small"
            borderRadius="50%"
            width="45px"
            height="45px"
            title="Next"
          />
        </Box>
      )}
      {previewUrl && !videoUrl && item.reducedResolution !== false && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          display="flex"
          justifyContent="center"
          paddingX="large"
          paddingTop="large"
          paddingBottom="large"
          zIndex={10000}
          style={{ background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0))', pointerEvents: 'none' }}
        >
          <Text color="white" fontSize="small" textAlign="center">
            {REDUCED_RESOLUTION_DISCLAIMER}
          </Text>
        </Box>
      )}
    </Box>
  )
}

FilePreviewOverlay.propTypes = {
  item: PropTypes.object,
  items: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
}

FilePreviewOverlay.defaultProps = {
  item: null,
  items: [],
  onNavigate: () => {},
}

export default FilePreviewOverlay
